import type {
  Collection,
  CollectionSource,
  CollectionSourceConfig,
  CollectionSourceAuth,
  WordPressExclusionRule,
  WordPressSiteConfig,
} from '@/types/source-types';
import type {
  WordPressSiteFormState,
  WordPressSiteFormErrors,
} from './wordpress-site-form';
import { validateCronExpression } from '@/lib/cron-utils';

export const DEFAULT_WORDPRESS_SCHEDULE = '0 0 * * *';

// WordPress users are not a post type; they are verified against
// /wp-json/wp/v2/users instead of the /types registry. Mirrors
// _USER_CONTENT_TYPES in qdrant-svc wordpress_cleanup.py.
const USER_CONTENT_TYPES = new Set(['user', 'users']);

export interface WordPressValidationOptions {
  requireConfig: boolean;
  isEditing?: boolean;
}

export interface WordPressValidationResult {
  errors: Record<string, WordPressSiteFormErrors>;
  config: CollectionSourceConfig | null;
  shouldClearAuth: boolean;
}

type CamelWordPressConfig = CollectionSourceConfig & {
  baseUrl?: string | null;
  scheduleCron?: string | null;
  auth?: (CollectionSourceAuth & { hasCredentials?: boolean }) | null;
};

const firstDefined = <T>(
  ...values: Array<T | null | undefined>
): T | undefined => {
  for (const value of values) {
    if (value !== undefined && value !== null) {
      return value;
    }
  }
  return undefined;
};

const toSanitizedString = (value: unknown, fallback = ''): string => {
  if (value === undefined || value === null) return fallback;
  return typeof value === 'string' ? value : String(value);
};

export interface ExtractedWordPressConfig {
  baseUrl: string;
  scheduleCron: string;
  cronEnabled: boolean;
  username: string;
  hasCredentials: boolean;
  ssmParameterArn?: string;
}

const pluckNestedWordPressConfig = (
  config: CollectionSourceConfig | null | undefined,
): CamelWordPressConfig | undefined => {
  if (!config) return undefined;
  const record = config as unknown as Record<string, unknown>;
  const candidates = [
    record.wordpress,
    record.wordpress_api,
    record.wordPress,
    record.wordpressApi,
  ];

  for (const candidate of candidates) {
    if (candidate && typeof candidate === 'object') {
      return candidate as CamelWordPressConfig;
    }
  }

  return undefined;
};

export const extractWordPressConfig = (
  config: CollectionSourceConfig | null | undefined,
): ExtractedWordPressConfig => {
  const extended = (config ?? {}) as CamelWordPressConfig;
  const nested = pluckNestedWordPressConfig(config);

  const auth = firstDefined(extended?.auth, nested?.auth) ?? undefined;
  const authRecord = auth as Record<string, unknown> | undefined;

  const baseUrlRaw = firstDefined(
    config?.base_url,
    extended.baseUrl,
    nested?.base_url,
    nested?.baseUrl,
  );
  const scheduleRaw = firstDefined(
    config?.schedule_cron,
    extended.scheduleCron,
    nested?.schedule_cron,
    nested?.scheduleCron,
  );
  const cronEnabledRaw = firstDefined(
    (config as Record<string, unknown>)?.cron_enabled,
    (extended as Record<string, unknown>)?.cronEnabled,
    (nested as Record<string, unknown>)?.cron_enabled,
    (nested as Record<string, unknown>)?.cronEnabled,
  );
  const usernameRaw = firstDefined(
    (auth as CollectionSourceAuth | undefined)?.username,
    authRecord?.username as string,
    authRecord?.userName as string,
    authRecord?.Username as string,
  );
  const hasCredentialsRaw = firstDefined(
    (auth as CollectionSourceAuth | undefined)?.has_credentials,
    authRecord?.hasCredentials as boolean,
  );
  const ssmArnRaw = firstDefined(
    (auth as CollectionSourceAuth | undefined)?.ssm_parameter_arn,
    authRecord?.ssmParameterArn as string,
  );

  const schedule = toSanitizedString(scheduleRaw).trim();

  return {
    baseUrl: toSanitizedString(baseUrlRaw).trim(),
    scheduleCron: schedule || DEFAULT_WORDPRESS_SCHEDULE,
    cronEnabled: cronEnabledRaw !== undefined ? Boolean(cronEnabledRaw) : true,
    username: toSanitizedString(usernameRaw).trim(),
    hasCredentials: Boolean(hasCredentialsRaw),
    ssmParameterArn: toSanitizedString(ssmArnRaw).trim() || undefined,
  };
};

export const hasWordPressConfig = (
  config: CollectionSourceConfig | null | undefined,
) => {
  // Check new multi-site format first
  if (config?.wordpress_sites && config.wordpress_sites.length > 0) {
    return true;
  }
  const extracted = extractWordPressConfig(config);
  return (
    Boolean(extracted.baseUrl) ||
    Boolean(extracted.username) ||
    extracted.hasCredentials
  );
};

const getNestedWordPressConfigFromCollection = (
  collection: Collection | null,
): CollectionSourceConfig | null => {
  if (!collection) return null;
  const possibleSources = [
    collection.source_config,
    collection.config as unknown as CollectionSourceConfig,
  ];
  for (const candidate of possibleSources) {
    if (!candidate) continue;
    if (hasWordPressConfig(candidate)) {
      return candidate;
    }

    const record = candidate as unknown as Record<string, unknown>;
    const nested =
      pluckNestedWordPressConfig(candidate) ??
      (record.wordpress as CollectionSourceConfig | undefined) ??
      (record.wordpress_api as CollectionSourceConfig | undefined) ??
      (record.wordPress as CollectionSourceConfig | undefined) ??
      (record.wordpressApi as CollectionSourceConfig | undefined);

    if (nested) {
      return nested;
    }
  }

  return null;
};

export const extractWordPressConfigFromCollection = (
  collection: Collection | null,
): ExtractedWordPressConfig => {
  const candidate = getNestedWordPressConfigFromCollection(collection);
  return extractWordPressConfig(candidate);
};

export const inferCollectionSource = (
  collection: Collection | null,
): CollectionSource => {
  if (collection?.source === 'wordpress_api') return 'wordpress_api';
  if (collection?.source === 'uploads') return 'uploads';
  if (hasWordPressConfig(getNestedWordPressConfigFromCollection(collection))) {
    return 'wordpress_api';
  }
  return 'uploads';
};

const isValidUrl = (value: string) => {
  try {
    const parsed = new URL(value);
    return Boolean(parsed.protocol) && Boolean(parsed.hostname);
  } catch {
    return false;
  }
};

const sanitize = (value: string) => value.trim();

// ── Multi-site factory and mapping functions ──

export function createEmptySiteState(): WordPressSiteFormState {
  return {
    siteId: crypto.randomUUID().slice(0, 12),
    label: '',
    baseUrl: '',
    scheduleCron: DEFAULT_WORDPRESS_SCHEDULE,
    cronEnabled: true,
    username: '',
    password: '',
    hasCredentials: false,
    isPasswordDirty: false,
    useAuth: false,
    postTypes: [],
    perPage: 10,
    exclusionRules: [],
    userContentField: '',
  };
}

export function mapConfigToSitesState(
  sourceConfig: CollectionSourceConfig | null | undefined,
): WordPressSiteFormState[] {
  if (!sourceConfig) return [];

  // New format: wordpress_sites array
  if (sourceConfig.wordpress_sites && sourceConfig.wordpress_sites.length > 0) {
    return sourceConfig.wordpress_sites.map((site) => ({
      siteId: site.site_id,
      label: site.label,
      baseUrl: site.base_url || '',
      scheduleCron: site.schedule_cron || DEFAULT_WORDPRESS_SCHEDULE,
      cronEnabled: site.cron_enabled ?? true,
      username: site.auth?.username || '',
      password: '',
      hasCredentials: site.auth?.has_credentials || false,
      isPasswordDirty: false,
      useAuth: !!(site.auth?.username || site.auth?.has_credentials),
      postTypes: site.wordpress_post_types || [],
      perPage: site.wordpress_per_page || 10,
      exclusionRules: site.wordpress_exclusion_rules || [],
      userContentField: site.user_content_field || '',
    }));
  }

  // Legacy flat format: convert to single-element array
  if (sourceConfig.base_url) {
    let hostname = '';
    try {
      hostname = new URL(sourceConfig.base_url).hostname;
    } catch {
      hostname = sourceConfig.base_url;
    }
    return [
      {
        siteId: crypto.randomUUID().slice(0, 12),
        label: hostname,
        baseUrl: sourceConfig.base_url,
        scheduleCron: sourceConfig.schedule_cron || DEFAULT_WORDPRESS_SCHEDULE,
        cronEnabled: sourceConfig.cron_enabled ?? true,
        username: sourceConfig.auth?.username || '',
        password: '',
        hasCredentials: sourceConfig.auth?.has_credentials || false,
        isPasswordDirty: false,
        useAuth: !!(
          sourceConfig.auth?.username || sourceConfig.auth?.has_credentials
        ),
        postTypes: sourceConfig.wordpress_post_types || [],
        perPage: sourceConfig.wordpress_per_page || 10,
        exclusionRules: sourceConfig.wordpress_exclusion_rules || [],
        userContentField: sourceConfig.user_content_field || '',
      },
    ];
  }

  return [];
}

/**
 * Map a Collection to site form states. Handles both new and legacy formats.
 */
export function mapCollectionToSitesState(
  collection: Collection | null,
): WordPressSiteFormState[] {
  if (!collection) return [];

  // Try source_config first
  const fromSourceConfig = mapConfigToSitesState(collection.source_config);
  if (fromSourceConfig.length > 0) return fromSourceConfig;

  // Fallback: extract from legacy nested structures
  const extracted = extractWordPressConfigFromCollection(collection);
  if (!extracted.baseUrl && !extracted.username && !extracted.hasCredentials) {
    return [];
  }

  const sourceConfig = collection?.source_config as Record<
    string,
    unknown
  > | null;
  const postTypes = sourceConfig?.wordpress_post_types;
  const perPage = sourceConfig?.wordpress_per_page;
  const exclusionRules = sourceConfig?.wordpress_exclusion_rules;
  const userContentField = sourceConfig?.user_content_field;

  let hostname = '';
  try {
    hostname = new URL(extracted.baseUrl).hostname;
  } catch {
    hostname = extracted.baseUrl;
  }

  return [
    {
      siteId: crypto.randomUUID().slice(0, 12),
      label: hostname,
      baseUrl: extracted.baseUrl,
      scheduleCron: extracted.scheduleCron || DEFAULT_WORDPRESS_SCHEDULE,
      cronEnabled: extracted.cronEnabled,
      username: extracted.username,
      password: '',
      hasCredentials: extracted.hasCredentials,
      isPasswordDirty: false,
      useAuth: extracted.hasCredentials || Boolean(extracted.username),
      postTypes: Array.isArray(postTypes) ? (postTypes as string[]) : [],
      perPage:
        typeof perPage === 'number' && perPage >= 10 && perPage <= 100
          ? perPage
          : 10,
      exclusionRules: Array.isArray(exclusionRules)
        ? (exclusionRules as WordPressExclusionRule[])
        : [],
      userContentField:
        typeof userContentField === 'string' ? userContentField : '',
    },
  ];
}

// ── Multi-site validation ──

export function validateWordPressSites(
  sites: WordPressSiteFormState[],
  options?: { isEditing?: boolean },
): Record<string, WordPressSiteFormErrors> {
  const allErrors: Record<string, WordPressSiteFormErrors> = {};

  for (const site of sites) {
    const siteErrors: WordPressSiteFormErrors = {};
    const baseUrl = sanitize(site.baseUrl);
    const schedule = sanitize(site.scheduleCron);
    const username = sanitize(site.username);
    const password = site.password.trim();

    if (!baseUrl) {
      siteErrors.baseUrl = 'Base URL is required';
    } else if (!isValidUrl(baseUrl)) {
      siteErrors.baseUrl = 'Enter a valid URL';
    }

    if (!schedule) {
      siteErrors.scheduleCron = 'Scrape cadence is required';
    } else {
      const cronValidation = validateCronExpression(schedule);
      if (!cronValidation.valid) {
        siteErrors.scheduleCron =
          cronValidation.error || 'Invalid cron expression';
      }
    }

    // For new collections: require both username and password when useAuth is true
    if (site.useAuth && !options?.isEditing) {
      if (!username) {
        siteErrors.username =
          'Username is required when authentication is enabled';
      }
      if (!password) {
        siteErrors.password =
          'Password is required when authentication is enabled';
      }
    }

    // For editing: validate auth fields
    if (site.useAuth && options?.isEditing) {
      if (site.isPasswordDirty && !password) {
        siteErrors.password = 'Enter a password or cancel the update';
      }
      if (password && !username) {
        siteErrors.username = 'Username is required when password is provided';
      }
      if (username && !site.hasCredentials && !password) {
        siteErrors.password = 'Password is required when username is provided';
      }
    }

    if (Object.keys(siteErrors).length > 0) {
      allErrors[site.siteId] = siteErrors;
    }
  }

  return allErrors;
}

// ── Build payload for API ──

export function buildSitesPayload(
  sites: WordPressSiteFormState[],
): CollectionSourceConfig {
  return {
    wordpress_sites: sites.map((site) => {
      let labelValue = site.label.trim();
      if (!labelValue) {
        try {
          labelValue = new URL(site.baseUrl).hostname;
        } catch {
          labelValue = site.baseUrl.trim();
        }
      }

      const sitePayload: WordPressSiteConfig = {
        site_id: site.siteId,
        label: labelValue,
        base_url: site.baseUrl.trim(),
        schedule_cron: site.scheduleCron.trim(),
        cron_enabled: site.cronEnabled,
        wordpress_post_types: site.postTypes.length > 0 ? site.postTypes : null,
        wordpress_per_page: site.perPage,
        wordpress_exclusion_rules:
          site.exclusionRules.length > 0 ? site.exclusionRules : null,
      };

      // Only emit user_content_field when actually set. Writing `null` for the
      // common empty case would make the rebuilt payload differ from a stored
      // config that never had the key, dirtying the JSON comparison in the edit
      // modal and triggering a spurious source_config PATCH on unrelated edits.
      const trimmedContentField = site.userContentField.trim();
      if (trimmedContentField) {
        sitePayload.user_content_field = trimmedContentField;
      }

      if (site.useAuth) {
        const auth: CollectionSourceAuth = {};
        const trimmedUsername = site.username.trim();
        if (trimmedUsername) {
          auth.username = trimmedUsername;
        }
        if (site.isPasswordDirty && site.password) {
          auth.password = site.password;
        } else if (site.hasCredentials) {
          auth.has_credentials = true;
        }
        if (Object.keys(auth).length > 0) {
          sitePayload.auth = auth;
        }
      }

      return sitePayload;
    }),
  };
}

// ── Legacy single-site validation (kept for backward compatibility) ──

export const validateWordPressConfig = (
  values: {
    baseUrl: string;
    scheduleCron: string;
    cronEnabled: boolean;
    username: string;
    password: string;
    hasCredentials: boolean;
    isPasswordDirty: boolean;
    useAuth: boolean;
    postTypes: string[];
    perPage: number;
  },
  options: { requireConfig: boolean; isEditing?: boolean },
): {
  errors: WordPressSiteFormErrors;
  config: CollectionSourceConfig | null;
  shouldClearAuth: boolean;
} => {
  const errors: WordPressSiteFormErrors = {};

  if (!options.requireConfig) {
    return { errors, config: null, shouldClearAuth: false };
  }

  const baseUrl = sanitize(values.baseUrl);
  const schedule = sanitize(values.scheduleCron);
  const username = sanitize(values.username);
  const password = values.password.trim();

  if (!baseUrl) {
    errors.baseUrl = 'Base URL is required';
  } else if (!isValidUrl(baseUrl)) {
    errors.baseUrl = 'Enter a valid URL';
  }

  if (!schedule) {
    errors.scheduleCron = 'Scrape cadence is required';
  } else {
    const cronValidation = validateCronExpression(schedule);
    if (!cronValidation.valid) {
      errors.scheduleCron = cronValidation.error || 'Invalid cron expression';
    }
  }

  if (values.useAuth && !options.isEditing) {
    if (!username) {
      errors.username = 'Username is required when authentication is enabled';
    }
    if (!password) {
      errors.password = 'Password is required when authentication is enabled';
    }
  }

  if (values.useAuth && options.isEditing) {
    if (values.isPasswordDirty && !password) {
      errors.password = 'Enter a password or cancel the update';
    }
    if (password && !username) {
      errors.username = 'Username is required when password is provided';
    }
    if (username && !values.hasCredentials && !password) {
      errors.password = 'Password is required when username is provided';
    }
  }

  if (Object.keys(errors).length > 0) {
    return { errors, config: null, shouldClearAuth: false };
  }

  const auth: Record<string, string> = {};
  const config: CollectionSourceConfig = {
    base_url: baseUrl,
    schedule_cron: schedule,
    cron_enabled: values.cronEnabled,
  };

  if (!values.useAuth) {
    return {
      errors,
      config,
      shouldClearAuth: values.hasCredentials,
    };
  }

  if (username) {
    auth.username = username;
  }

  if (password) {
    auth.password = password;
  }

  if (Object.keys(auth).length > 0) {
    config.auth = auth;
  }

  return {
    errors,
    config,
    shouldClearAuth: false,
  };
};

export const extractExclusionRules = (
  collection: Collection | null,
): WordPressExclusionRule[] => {
  const sourceConfig = collection?.source_config;
  if (!sourceConfig) return [];

  // New format: rules live inside each wordpress_sites entry
  if (sourceConfig.wordpress_sites && sourceConfig.wordpress_sites.length > 0) {
    return sourceConfig.wordpress_sites.flatMap(
      (site) => site.wordpress_exclusion_rules || [],
    );
  }

  // Legacy flat format
  const rules = sourceConfig.wordpress_exclusion_rules;
  if (Array.isArray(rules)) {
    return rules;
  }
  return [];
};

/**
 * Extract exclusion rules for a specific site within a collection.
 */
export const extractSiteExclusionRules = (
  collection: Collection | null,
  siteId: string,
): WordPressExclusionRule[] => {
  const sites = collection?.source_config?.wordpress_sites;
  if (!sites) return [];
  const site = sites.find((s) => s.site_id === siteId);
  return site?.wordpress_exclusion_rules || [];
};

export interface WordPressConnectionTestResult {
  success: boolean;
  message: string;
  statusCode?: number;
}

export const testWordPressConnection = async (
  baseUrl: string,
  username?: string,
  password?: string,
): Promise<WordPressConnectionTestResult> => {
  if (!baseUrl.trim()) {
    return {
      success: false,
      message: 'Base URL is required',
    };
  }

  try {
    // Ensure URL has proper format
    const url = new URL(baseUrl);
    const testEndpoint = `${url.origin}/wp-json/wp/v2/`;

    // Prepare headers
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    // Add Basic Auth if credentials provided
    if (username && password) {
      const encodedCredentials = btoa(`${username}:${password}`);
      headers['Authorization'] = `Basic ${encodedCredentials}`;
    }

    const response = await fetch(testEndpoint, {
      method: 'GET',
      headers,
      mode: 'cors',
      credentials: username && password ? 'include' : 'omit',
    });

    if (response.ok) {
      return {
        success: true,
        message: 'Connection successful',
        statusCode: response.status,
      };
    }

    // Handle specific status codes
    if (response.status === 401) {
      return {
        success: false,
        message: 'Authentication required or credentials invalid',
        statusCode: response.status,
      };
    }

    if (response.status === 403) {
      return {
        success: false,
        message: 'Access forbidden',
        statusCode: response.status,
      };
    }

    if (response.status === 404) {
      return {
        success: false,
        message: 'WordPress REST API not found at this URL',
        statusCode: response.status,
      };
    }

    return {
      success: false,
      message: `Connection failed with status ${response.status}`,
      statusCode: response.status,
    };
  } catch (error) {
    if (
      error instanceof TypeError &&
      error.message.includes('Failed to fetch')
    ) {
      return {
        success: false,
        message:
          'Network error: Unable to reach WordPress site. Check URL or CORS settings.',
      };
    }

    if (error instanceof Error) {
      return {
        success: false,
        message: `Error: ${error.message}`,
      };
    }

    return {
      success: false,
      message: 'Unknown error occurred',
    };
  }
};

export interface WordPressPostTypesTestResult {
  success: boolean;
  message: string;
  availableTypes?: string[];
  missingTypes?: string[];
}

export const testWordPressPostTypes = async (
  baseUrl: string,
  selectedTypes: string[],
  username?: string,
  password?: string,
): Promise<WordPressPostTypesTestResult> => {
  if (!baseUrl.trim()) {
    return {
      success: false,
      message: 'Base URL is required',
    };
  }

  if (!selectedTypes || selectedTypes.length === 0) {
    return {
      success: false,
      message: 'No post types selected to test',
    };
  }

  try {
    // Ensure URL has proper format
    const url = new URL(baseUrl);
    const typesEndpoint = `${url.origin}/wp-json/wp/v2/types`;

    // Prepare headers
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    // Add Basic Auth if credentials provided
    if (username && password) {
      const encodedCredentials = btoa(`${username}:${password}`);
      headers['Authorization'] = `Basic ${encodedCredentials}`;
    }

    // `users` is not a post type — it lives at /wp-json/wp/v2/users, not in the
    // /types registry. WordPress sites routinely block the users endpoint
    // cross-origin (user-enumeration hardening), so a browser fetch always trips
    // CORS even when server-side ingestion auth succeeds. Accept it as a valid
    // special type without a browser round-trip; ingestion verifies it
    // server-side. Mirrors _USER_CONTENT_TYPES in qdrant-svc wordpress_cleanup.py.
    const verifiedUserTypes = selectedTypes.filter((type) =>
      USER_CONTENT_TYPES.has(type.toLowerCase()),
    );
    const postTypes = selectedTypes.filter(
      (type) => !USER_CONTENT_TYPES.has(type.toLowerCase()),
    );

    if (postTypes.length === 0) {
      return {
        success: true,
        message: `All ${selectedTypes.length} post type${selectedTypes.length === 1 ? '' : 's'} exist`,
        availableTypes: verifiedUserTypes,
        missingTypes: [],
      };
    }

    const response = await fetch(typesEndpoint, {
      method: 'GET',
      headers,
      mode: 'cors',
      credentials: username && password ? 'include' : 'omit',
    });

    if (!response.ok) {
      if (response.status === 401) {
        return {
          success: false,
          message: 'Authentication required to access post types',
        };
      }

      if (response.status === 403) {
        return {
          success: false,
          message: 'Access forbidden when fetching post types',
        };
      }

      if (response.status === 404) {
        return {
          success: false,
          message: 'Post types endpoint not found',
        };
      }

      return {
        success: false,
        message: `Failed to fetch post types (status ${response.status})`,
      };
    }

    const data = await response.json();

    // WordPress returns post types as an object with post type slugs as keys
    const availableTypes = Object.keys(data);
    const availableTypesLower = new Set(
      availableTypes.map((type) => type.toLowerCase()),
    );

    // Check which selected post types exist (case-insensitive)
    const missingTypes = postTypes.filter(
      (type) => !availableTypesLower.has(type.toLowerCase()),
    );

    if (missingTypes.length > 0) {
      return {
        success: false,
        message: `Post types not found: ${missingTypes.join(', ')}`,
        availableTypes: [...availableTypes, ...verifiedUserTypes],
        missingTypes,
      };
    }

    return {
      success: true,
      message: `All ${selectedTypes.length} post type${selectedTypes.length === 1 ? '' : 's'} exist`,
      availableTypes: [...availableTypes, ...verifiedUserTypes],
      missingTypes: [],
    };
  } catch (error) {
    if (
      error instanceof TypeError &&
      error.message.includes('Failed to fetch')
    ) {
      return {
        success: false,
        message: 'Network error: Unable to reach WordPress site',
      };
    }

    if (error instanceof Error) {
      return {
        success: false,
        message: `Error: ${error.message}`,
      };
    }

    return {
      success: false,
      message: 'Unknown error occurred',
    };
  }
};
